/**
 * Просмотр историй собеседника.
 *
 * ── Зачем ───────────────────────────────────────────────────────────────────
 *
 * Клиенты выкладывают в истории то, о чём потом спрашивают в переписке:
 * акции, товар, объявления. Менеджеру без Telegram Desktop их не увидеть, и
 * разговор идёт вслепую.
 *
 * ── Что здесь как в Telegram, а что нет ─────────────────────────────────────
 *
 * Истории идут подряд, с полосками прогресса сверху; фото держится пять
 * секунд, видео — сколько длится. Стрелки и клик по краям листают, Escape
 * закрывает. Просмотр засчитывается на стороне Telegram при загрузке файла:
 * автор увидит, что рабочий аккаунт посмотрел историю — как с любого клиента.
 * Ответить на историю отсюда нельзя: это отдельный вид сообщения, и его
 * место в очереди на потом.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { StoryDto } from '../lib/api.js';
import { RichText } from './RichText.tsx';
import { XIcon } from './icons.tsx';
import { cn } from '@/lib/utils';

interface StoryViewerProps {
  chatId: string;
  title: string;
  stories: StoryDto[];
  onClose: () => void;
}

const PHOTO_SECONDS = 5;

export function StoryViewer({ chatId, title, stories, onClose }: StoryViewerProps) {
  // Начинаем с первой непросмотренной — как Telegram; если все просмотрены, с первой.
  const firstUnread = Math.max(0, stories.findIndex((s) => !s.viewed));
  const [index, setIndex] = useState(firstUnread === -1 ? 0 : firstUnread);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const story = stories[index];
  const src = story ? `/api/chats/${chatId}/stories/${story.id}/media` : '';

  const next = useCallback(() => {
    if (index + 1 >= stories.length) onClose();
    else setIndex(index + 1);
  }, [index, stories.length, onClose]);

  const prev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') next();
      if (event.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, next, prev]);

  // Таймер фотографии. У видео прогресс берётся из самого проигрывателя.
  useEffect(() => {
    setProgress(0);
    if (!story || story.kind !== 'photo') return;

    const started = Date.now();
    let elapsedBeforePause = 0;
    let pausedAt: number | null = null;
    const timer = setInterval(() => {
      if (paused) {
        pausedAt ??= Date.now();
        return;
      }
      if (pausedAt !== null) {
        elapsedBeforePause += Date.now() - pausedAt;
        pausedAt = null;
      }
      const ratio = (Date.now() - started - elapsedBeforePause) / (PHOTO_SECONDS * 1000);
      if (ratio >= 1) {
        clearInterval(timer);
        next();
      } else {
        setProgress(ratio);
      }
    }, 50);
    return () => clearInterval(timer);
    // next меняется вместе с index — таймер и должен перезапускаться на каждой истории.
  }, [story, paused, next]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (paused) void video.pause();
    else void video.play().catch(() => undefined);
  }, [paused, index]);

  if (!story) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Истории: ${title}`}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={() => setPaused(true)}
        onMouseUp={() => setPaused(false)}
        onMouseLeave={() => setPaused(false)}
        className="relative flex h-full max-h-[92vh] w-full max-w-[440px] select-none flex-col overflow-hidden rounded-2xl bg-black"
      >
        {/* Полоски прогресса — по одной на историю, как в Telegram. */}
        <div className="absolute inset-x-2 top-2 z-10 flex gap-1">
          {stories.map((s, i) => (
            <span key={s.id} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full bg-white"
                style={{ width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%' }}
              />
            </span>
          ))}
        </div>

        <div className="absolute inset-x-3 top-5 z-10 flex items-center justify-between text-white">
          <span className="text-[13px] font-medium drop-shadow">
            {title}
            <span className="ml-2 text-white/70">{formatStoryTime(story.date)}</span>
          </span>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={onClose}
            className="grid size-8 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
          >
            <XIcon className="size-4" />
          </button>
        </div>

        {story.kind === 'video' ? (
          <video
            key={story.id}
            ref={videoRef}
            src={src}
            autoPlay
            playsInline
            onTimeUpdate={(e) => {
              const v = e.currentTarget;
              if (v.duration) setProgress(v.currentTime / v.duration);
            }}
            onEnded={next}
            className="h-full w-full object-contain"
          />
        ) : (
          <img key={story.id} src={src} alt="История" className="h-full w-full object-contain" />
        )}

        {/* Края листают: левая треть — назад, правая — вперёд. */}
        <button type="button" aria-label="Назад" onClick={prev} className="absolute inset-y-0 left-0 w-1/3 cursor-w-resize" />
        <button type="button" aria-label="Дальше" onClick={next} className="absolute inset-y-0 right-0 w-1/3 cursor-e-resize" />

        {story.caption && (
          <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-10 text-[13.5px] leading-relaxed text-white">
            <RichText text={story.caption} entities={story.entities} chatId={chatId} />
          </div>
        )}

        <span
          className={cn(
            'pointer-events-none absolute bottom-3 right-3 z-10 text-[11px] text-white/60',
            stories.length < 2 && 'hidden',
          )}
        >
          {index + 1} / {stories.length}
        </span>
      </div>
    </div>,
    document.body,
  );
}

function formatStoryTime(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return `${Math.max(1, Math.floor(diff / 60_000))} мин назад`;
  if (hours < 24) return `${hours} ч назад`;
  return new Date(iso).toLocaleDateString('ru-RU');
}
